import { Component, OnInit, Input, Self, Optional, ViewChild, Output, EventEmitter, ElementRef } from '@angular/core';
import { NgControl, ControlValueAccessor } from '@angular/forms';
import * as uuid from 'uuid';

@Component({
    selector: 'app-form-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
})
export class FormInputComponent implements OnInit, ControlValueAccessor {
    public onChange: (x?: any) => void;

    public onTouched: (x?: any) => void;

    public id: string;

    public _value: any = '';

    @Input() disabled: boolean;

    @Input() label: string;

    @Input() placeholder: string;

    @Input() popoverTitle: string;

    @Input() popoverBody: string;

    @Input() hint: string;

    @Input() type: 'text' | 'email' | 'password' | 'phone' | 'number' = 'text';

    @Input() validation = true;

    @Input() messages = false;

    @Input() charLimit: number;

    @Input() invalidPass: boolean;

    @Input() invalidText: string;

    @Output('change') changeOutput = new EventEmitter<any>();

    @ViewChild('input', { static: true }) input: ElementRef;

    public passwordVisible = false;

    get currentCharactersAmount(): number {
        return this._value?.length ?? 0;
    }

    get hasMaxLengthError(): boolean {
        return this.ngControl.getError('maxlength');
    }

    get resolvedType(): string {
        if (this.type === 'password') {
            return this.passwordVisible ? 'text' : 'password';
        }

        return this.type;
    }

    get shouldShowPasswordToggle(): boolean {
        return this.type === 'password';
    }

    get passwordToggleIcon(): string {
        return this.passwordVisible ? 'visibility_off' : 'visibility';
    }

    get passwordToggleAriaLabel(): string {
        return this.passwordVisible ? 'Hide password' : 'Show password';
    }

    constructor(@Self() @Optional() private ngControl: NgControl) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit(): void {
        this.id = uuid.v4();
    }

    get invalid(): boolean {
        if (this.ngControl) {
            return this.ngControl.invalid;
        }
        return false;
    }

    get valid(): boolean {
        if (this.ngControl) {
            return this.ngControl.valid;
        }
        return false;
    }

    get pristine(): boolean {
        if (this.ngControl) {
            return this.ngControl.pristine;
        }
        return false;
    }

    get dirty(): boolean {
        if (this.ngControl) {
            return this.ngControl.dirty;
        }
        return false;
    }

    get touched(): boolean {
        if (this.ngControl) {
            return this.ngControl.touched;
        }
        return false;
    }

    get value(): any {
        return this._value;
    }

    set value(v) {
        this._value = v;
        if (this.onChange) {
            this.changeOutput.emit(v);
            this.onChange(v);
            this.onTouched();
        }
    }

    valueChange(v): void {
        this.value = v;
    }

    writeValue(value: any): void {
        this._value = value;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    registerOnChange(fn: any): void {
        // Store the provided function as an internal method.
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        // Store the provided function as an internal method.
        this.onTouched = fn;
    }

    togglePasswordVisibility(): void {
        if (!this.shouldShowPasswordToggle) {
            return;
        }

        this.passwordVisible = !this.passwordVisible;

        if (this.input?.nativeElement) {
            this.input.nativeElement.focus();
        }
    }
}
